/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import Icon from "@material-ui/core/Icon";
import Chip from "@material-ui/core/Chip";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import LinearProgress from "@material-ui/core/LinearProgress";
import GroupIcon from "@material-ui/icons/Group";
import Button from "components/CustomButtons/Button.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import SnackbarContent from "components/Snackbar/SnackbarContent.jsx";
import { dayjs } from "utils/time.jsx";
import {
  createMission,
  createMissionGoal,
  getMissions,
  getUserGroups,
  updateMission,
  updateMissionGoal
} from "services/groupMissionsDataProvider.js";
import { getGoals } from "services/goalsDataProvider.js";
import {
  grayColor,
  whiteColor,
  cardTitle as sharedCardTitle,
  cardSubtitle as sharedCardSubtitle,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

const missionPalette = {
  bodyText: grayColor[2],
  softText: grayColor[14],
  softSurface: grayColor[12],
  borderSubtle: grayColor[8],
  borderCard: grayColor[15],
  surface: whiteColor
};

const styles = theme => ({
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap"
  },
  actionRow: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  pageHeader: {
    marginBottom: "18px"
  },
  cardTitle: {
    ...sharedCardTitle,
    margin: 0,
    minHeight: "auto",
    color: "inherit",
    fontWeight: 600
  },
  cardSubtitle: {
    ...sharedCardSubtitle,
    marginTop: "6px",
    opacity: 0.85,
    color: "inherit"
  },
  bodyText: {
    color: missionPalette.bodyText
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "10px",
    marginTop: "12px"
  },
  metaCard: {
    background: missionPalette.softSurface,
    borderRadius: "10px",
    padding: "10px 12px",
    border: "1px solid " + missionPalette.borderSubtle
  },
  metaLabel: {
    color: missionPalette.softText,
    fontSize: "12px",
    marginBottom: "4px"
  },
  goalList: {
    marginTop: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  goalTilesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
    gap: "10px"
  },
  goalItem: {
    border: "1px solid " + missionPalette.borderCard,
    borderRadius: "10px",
    padding: "10px 12px",
    background: missionPalette.surface
  },
  goalTileTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px"
  },
  goalNameRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  goalTimeLeft: {
    fontSize: "12px",
    color: missionPalette.softText,
    whiteSpace: "nowrap"
  },
  goalDescription: {
    marginTop: "8px",
    marginBottom: "8px",
    color: missionPalette.bodyText,
    fontSize: "13px"
  },
  goalMeta: {
    fontSize: "12px",
    color: missionPalette.softText,
    marginTop: "8px"
  },
  goalActions: {
    marginTop: "8px",
    display: "flex",
    justifyContent: "space-between",
    gap: "8px"
  },
  goalItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "8px",
    marginBottom: "4px"
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px"
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15, 23, 42, 0.48)",
    padding: "16px"
  },
  modalBox: {
    backgroundColor: missionPalette.surface,
    borderRadius: "12px",
    width: "100%",
    maxWidth: "760px",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    padding: "24px",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.22)"
  },
  modalTitle: {
    marginTop: 0,
    marginBottom: "18px"
  },
  formField: {
    marginBottom: "16px"
  },
  checkboxList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "6px",
    marginBottom: "16px"
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px"
  },
  footerActions: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    alignItems: "center"
  },
  footerButtonGroup: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap"
  },
  editButton: {
    padding: "0 6px",
    minWidth: 0
  },
  selectMenuPaper: {
    backgroundColor: missionPalette.surface,
    color: missionPalette.bodyText
  },
  emptyState: {
    padding: "24px",
    textAlign: "center",
    color: missionPalette.softText
  },
  helperText: {
    marginTop: "-6px",
    marginBottom: "12px",
    color: missionPalette.softText,
    fontSize: "12px"
  },
  ...(theme.palette && theme.palette.type === "dark"
    ? darkModeStyleBlocks.missions
    : {})
});

const defaultMissionForm = {
  name: "",
  mission_type: "CUSTOM",
  activity_type: "ALL",
  description: "",
  mission_scope: "SELF",
  group_id: "",
  group_ids: [],
  start_date: dayjs().format("YYYY-MM-DDTHH:mm"),
  end_date: dayjs()
    .add(7, "day")
    .format("YYYY-MM-DDTHH:mm")
};

const defaultGoalForm = {
  mission: "",
  name: "",
  goal_type: "DISTANCE",
  description: "",
  target_metric: "kilometers",
  target_value: "",
  target_date: "",
  is_shared: true,
  is_public: false
};

class Missions extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      missions: [],
      goals: [],
      groups: [],
      loading: true,
      missionModalOpen: false,
      goalModalOpen: false,
      missionForm: { ...defaultMissionForm },
      goalForm: { ...defaultGoalForm },
      editingMission: null,
      editingGoal: null,
      saving: false,
      error: "",
      success: ""
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    try {
      this.setState({ loading: true, error: "" });
      const [missionResponse, groupResponse, goalResponse] = await Promise.all([
        getMissions(this.props.axios),
        getUserGroups(this.props.axios),
        getGoals(this.props.axios, false)
      ]);
      this.setState({
        missions: missionResponse.results || missionResponse || [],
        groups: groupResponse.results || groupResponse || [],
        goals: goalResponse.results || goalResponse || [],
        loading: false
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: "Failed to load missions and groups."
      });
    }
  };

  openMissionModal = (mission = null) => {
    if (mission) {
      const scope = mission.multi_group
        ? "MULTI_GROUP"
        : mission.group
          ? "GROUP"
          : "SELF";
      this.setState({
        missionModalOpen: true,
        editingMission: mission,
        missionForm: {
          name: mission.name || "",
          mission_type: mission.mission_type || "CUSTOM",
          activity_type: mission.activity_type || "ALL",
          description: mission.description || "",
          mission_scope: scope,
          group_id: mission.group ? String(mission.group) : "",
          group_ids: mission.groups || [],
          start_date: mission.start_date
            ? dayjs(mission.start_date).format("YYYY-MM-DDTHH:mm")
            : dayjs().format("YYYY-MM-DDTHH:mm"),
          end_date: mission.end_date
            ? dayjs(mission.end_date).format("YYYY-MM-DDTHH:mm")
            : dayjs()
                .add(7, "day")
                .format("YYYY-MM-DDTHH:mm")
        },
        error: "",
        success: ""
      });
      return;
    }

    this.setState({
      missionModalOpen: true,
      editingMission: null,
      missionForm: { ...defaultMissionForm },
      error: "",
      success: ""
    });
  };

  closeMissionModal = () => {
    this.setState({
      missionModalOpen: false,
      editingMission: null,
      missionForm: { ...defaultMissionForm }
    });
  };

  openGoalModal = (missionId, goal = null) => {
    if (goal) {
      this.setState({
        goalModalOpen: true,
        editingGoal: goal,
        goalForm: {
          mission: goal.mission || missionId || "",
          name: goal.name || "",
          goal_type: goal.goal_type || "DISTANCE",
          description: goal.description || "",
          target_metric: goal.target_metric || "kilometers",
          target_value:
            goal.target_value != null ? String(goal.target_value) : "",
          target_date: goal.target_date
            ? dayjs(goal.target_date).format("YYYY-MM-DD")
            : "",
          is_shared: goal.is_shared !== undefined ? goal.is_shared : true,
          is_public: goal.is_public !== undefined ? goal.is_public : false
        },
        error: "",
        success: ""
      });
      return;
    }

    this.setState({
      goalModalOpen: true,
      editingGoal: null,
      goalForm: {
        ...defaultGoalForm,
        mission: missionId || ""
      },
      error: "",
      success: ""
    });
  };

  closeGoalModal = () => {
    this.setState({
      goalModalOpen: false,
      editingGoal: null,
      goalForm: { ...defaultGoalForm }
    });
  };

  handleMissionChange = field => event => {
    const value = event.target.value;
    this.setState({
      missionForm: {
        ...this.state.missionForm,
        [field]: value,
        ...(field === "mission_scope" && value !== "GROUP"
          ? { group_id: "" }
          : {}),
        ...(field === "mission_scope" && value !== "MULTI_GROUP"
          ? { group_ids: [] }
          : {})
      }
    });
  };

  handleGoalChange = field => event => {
    const target = event.target;
    const value = target.type === "checkbox" ? target.checked : target.value;
    this.setState({
      goalForm: {
        ...this.state.goalForm,
        [field]: value
      }
    });
  };

  toggleMissionGroup = groupId => () => {
    const currentIds = this.state.missionForm.group_ids || [];
    const nextIds = currentIds.includes(groupId)
      ? currentIds.filter(id => id !== groupId)
      : currentIds.concat(groupId);

    this.setState({
      missionForm: {
        ...this.state.missionForm,
        group_ids: nextIds
      }
    });
  };

  getSelectProps = () => {
    return {
      MenuProps: {
        PaperProps: {
          className: this.props.classes.selectMenuPaper
        }
      }
    };
  };

  submitMission = async () => {
    const { missionForm } = this.state;
    if (!missionForm.name.trim()) {
      this.setState({ error: "Mission name is required." });
      return;
    }

    try {
      this.setState({ saving: true, error: "", success: "" });
      const { editingMission } = this.state;
      const payload = {
        name: missionForm.name.trim(),
        mission_type: missionForm.mission_type,
        activity_type: missionForm.activity_type,
        description: missionForm.description.trim() || null,
        mission_scope: missionForm.mission_scope,
        group_id:
          missionForm.mission_scope === "GROUP" && missionForm.group_id
            ? parseInt(missionForm.group_id, 10)
            : null,
        group_ids:
          missionForm.mission_scope === "MULTI_GROUP"
            ? missionForm.group_ids
            : [],
        start_date: missionForm.start_date,
        end_date: missionForm.end_date
      };
      if (editingMission) {
        await updateMission(this.props.axios, editingMission.id, payload);
      } else {
        await createMission(this.props.axios, payload);
      }
      this.setState({
        saving: false,
        success: editingMission
          ? "Mission updated successfully."
          : "Mission created successfully."
      });
      this.closeMissionModal();
      this.loadData();
    } catch (error) {
      this.setState({
        saving: false,
        error: "Failed to create mission or challenge."
      });
    }
  };

  submitGoal = async () => {
    const { goalForm } = this.state;
    if (!goalForm.mission || !goalForm.name.trim() || !goalForm.target_value) {
      this.setState({
        error: "Mission, goal name, and target value are required."
      });
      return;
    }

    try {
      this.setState({ saving: true, error: "", success: "" });
      const { editingGoal } = this.state;
      const payload = {
        mission: parseInt(goalForm.mission, 10),
        name: goalForm.name.trim(),
        goal_type: goalForm.goal_type,
        description: goalForm.description.trim() || null,
        target_metric: goalForm.target_metric,
        target_value: parseFloat(goalForm.target_value),
        target_date: goalForm.target_date || null,
        is_shared: goalForm.is_shared,
        is_public: goalForm.is_public
      };
      if (editingGoal) {
        await updateMissionGoal(this.props.axios, editingGoal.id, payload);
      } else {
        await createMissionGoal(this.props.axios, payload);
      }
      this.setState({
        saving: false,
        success: editingGoal
          ? "Goal updated successfully."
          : "Goal created successfully."
      });
      this.closeGoalModal();
      this.loadData();
    } catch (error) {
      this.setState({
        saving: false,
        error: "Failed to create mission goal."
      });
    }
  };

  getScopeLabel = mission => {
    if (mission.multi_group) {
      return "Multi-group challenge";
    }
    if (mission.group) {
      return "Single group mission";
    }
    return "Self mission";
  };

  getGroupNameMap = () => {
    return this.state.groups.reduce((accumulator, group) => {
      accumulator[group.id] = group.name;
      return accumulator;
    }, {});
  };

  getMissionGoals = missionId => {
    return (this.state.goals || []).filter(goal => goal.mission === missionId);
  };

  openGoalDetails = goalId => {
    this.props.history.push(`/my-goals/${goalId}/details`);
  };

  renderMissionGroups = mission => {
    const { classes } = this.props;
    const groupNameMap = this.getGroupNameMap();
    const groupIds = [];

    if (mission.group) {
      groupIds.push(mission.group);
    }
    if (mission.groups && mission.groups.length) {
      mission.groups.forEach(groupId => {
        if (!groupIds.includes(groupId)) {
          groupIds.push(groupId);
        }
      });
    }

    if (!groupIds.length) {
      return <div className={classes.metaLabel}>No group attached</div>;
    }

    return (
      <div className={classes.chipRow}>
        {groupIds.map(groupId => (
          <Chip
            key={`mission-group-${mission.id}-${groupId}`}
            label={groupNameMap[groupId] || `Group ${groupId}`}
            size="small"
          />
        ))}
      </div>
    );
  };

  renderMissionGoal = (mission, goal) => {
    const { classes } = this.props;
    const daysLeft = goal.target_date
      ? dayjs(goal.target_date).diff(dayjs(), "day")
      : null;
    const percentageCompletion =
      (goal.metadata && goal.metadata.percentage_completion) || 0;

    return (
      <div className={classes.goalItem} key={`goal-${goal.id}`}>
        <div className={classes.goalTileTop}>
          <div className={classes.goalNameRow}>
            <Icon style={{ fontSize: "16px" }}>flag</Icon>
            <strong>{goal.name}</strong>
            {goal.is_shared ? <GroupIcon style={{ fontSize: "14px" }} /> : null}
          </div>
          <div className={classes.goalTimeLeft}>
            {daysLeft == null
              ? "No target date"
              : daysLeft >= 0
                ? `${daysLeft} days left`
                : "Target date passed"}
          </div>
        </div>
        <div className={classes.goalDescription}>
          {goal.description || "No description provided."}
        </div>
        <div className={classes.goalMeta}>
          <strong>Target:</strong> {goal.target_value} {goal.target_metric}
          {goal.target_date
            ? ` by ${dayjs(goal.target_date).format("MMM D, YYYY")}`
            : ""}
        </div>
        <LinearProgress variant="determinate" value={percentageCompletion} />
        <div className={classes.goalMeta}>
          <strong>Completion:</strong> {percentageCompletion.toFixed(1)}%
        </div>
        <div className={classes.goalActions}>
          <Button
            size="sm"
            color="info"
            onClick={() => this.openGoalDetails(goal.id)}
          >
            Details
          </Button>
          <Button
            size="sm"
            color="transparent"
            className={classes.editButton}
            onClick={() => this.openGoalModal(mission.id, goal)}
          >
            Edit
          </Button>
        </div>
      </div>
    );
  };

  renderMissionCard = mission => {
    const { classes } = this.props;
    const missionGoals = this.getMissionGoals(mission.id);

    return (
      <GridItem xs={12} sm={12} md={6} key={`mission-${mission.id}`}>
        <Card>
          <CardBody>
            <h4 className={classes.cardTitle}>{mission.name}</h4>
            <p className={classes.bodyText}>
              {mission.description || "No description provided."}
            </p>

            <div className={classes.metaGrid}>
              <div className={classes.metaCard}>
                <div className={classes.metaLabel}>Scope</div>
                <div>{this.getScopeLabel(mission)}</div>
              </div>
              <div className={classes.metaCard}>
                <div className={classes.metaLabel}>Type</div>
                <div>{mission.mission_type || "CUSTOM"}</div>
              </div>
              <div className={classes.metaCard}>
                <div className={classes.metaLabel}>Activity</div>
                <div>{mission.activity_type || "ALL"}</div>
              </div>
              <div className={classes.metaCard}>
                <div className={classes.metaLabel}>Window</div>
                <div>
                  {dayjs(mission.start_date).format("MMM D")}
                  {" - "}
                  {dayjs(mission.end_date).format("MMM D")}
                </div>
              </div>
            </div>

            <div className={classes.metaLabel} style={{ marginTop: "14px" }}>
              Groups
            </div>
            {this.renderMissionGroups(mission)}

            <div className={classes.goalList}>
              <div className={classes.metaLabel}>Goals</div>
              {missionGoals.length ? (
                <div className={classes.goalTilesGrid}>
                  {missionGoals.map(goal =>
                    this.renderMissionGoal(mission, goal)
                  )}
                </div>
              ) : (
                <div className={classes.metaLabel}>No goals attached yet.</div>
              )}
            </div>
          </CardBody>
          <CardFooter>
            <div className={classes.footerActions}>
              <div className={classes.metaLabel}>
                {missionGoals.length} linked goal
                {missionGoals.length === 1 ? "" : "s"}
              </div>
              <div className={classes.footerButtonGroup}>
                <Button
                  color="transparent"
                  size="sm"
                  onClick={() => this.openMissionModal(mission)}
                >
                  Edit
                </Button>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => this.openGoalModal(mission.id)}
                >
                  Add Goal
                </Button>
              </div>
            </div>
          </CardFooter>
        </Card>
      </GridItem>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      missions,
      groups,
      loading,
      missionModalOpen,
      goalModalOpen,
      missionForm,
      goalForm,
      editingMission,
      editingGoal,
      saving,
      error,
      success
    } = this.state;

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          {error ? <SnackbarContent message={error} color="danger" /> : null}
          {success ? (
            <SnackbarContent message={success} color="success" />
          ) : null}
          <div className={classes.pageHeader}>
            <div className={classes.headerRow}>
              <div>
                <h4 className={classes.cardTitle}>Missions &amp; Challenges</h4>
                <p className={classes.cardSubtitle}>
                  Create self missions, group challenges, and attach measurable
                  goals.
                </p>
              </div>
              <div className={classes.actionRow}>
                <Button color="info" onClick={() => this.openGoalModal("")}>
                  Create Goal
                </Button>
                <Button color="rose" onClick={() => this.openMissionModal()}>
                  Create Mission
                </Button>
              </div>
            </div>
          </div>
          {loading ? (
            <div className={classes.emptyState}>Loading missions...</div>
          ) : missions.length ? (
            <GridContainer>
              {missions.map(mission => this.renderMissionCard(mission))}
            </GridContainer>
          ) : (
            <div className={classes.emptyState}>
              {
                "No missions yet. Create a self mission or a group challenge to get started."
              }
            </div>
          )}
        </GridItem>

        <Modal
          className={classes.modal}
          open={missionModalOpen}
          onClose={this.closeMissionModal}
        >
          <Box className={classes.modalBox}>
            <h3 className={classes.modalTitle}>
              {editingMission ? "Edit Mission" : "Create Mission / Challenge"}
            </h3>
            <TextField
              className={classes.formField}
              fullWidth
              label="Mission name"
              value={missionForm.name}
              onChange={this.handleMissionChange("name")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Mission type"
              value={missionForm.mission_type}
              onChange={this.handleMissionChange("mission_type")}
              SelectProps={this.getSelectProps()}
            >
              <MenuItem value="WEEKLY_CHALLENGE">Weekly Challenge</MenuItem>
              <MenuItem value="MONTHLY_CHALLENGE">Monthly Challenge</MenuItem>
              <MenuItem value="YEARLY_CHALLENGE">Yearly Challenge</MenuItem>
              <MenuItem value="CUSTOM">Custom</MenuItem>
            </TextField>
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Activity type"
              value={missionForm.activity_type}
              onChange={this.handleMissionChange("activity_type")}
              SelectProps={this.getSelectProps()}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="RUN">Run</MenuItem>
              <MenuItem value="WALK">Walk</MenuItem>
              <MenuItem value="CYCLE">Cycle</MenuItem>
              <MenuItem value="SWIM">Swim</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Mission scope"
              value={missionForm.mission_scope}
              onChange={this.handleMissionChange("mission_scope")}
              SelectProps={this.getSelectProps()}
            >
              <MenuItem value="SELF">Self</MenuItem>
              <MenuItem value="GROUP">Single Group</MenuItem>
              <MenuItem value="MULTI_GROUP">Multi Group</MenuItem>
            </TextField>
            {missionForm.mission_scope === "GROUP" ? (
              <TextField
                className={classes.formField}
                fullWidth
                select
                label="Group"
                value={missionForm.group_id}
                onChange={this.handleMissionChange("group_id")}
                SelectProps={this.getSelectProps()}
              >
                {groups.map(group => (
                  <MenuItem value={group.id} key={`group-option-${group.id}`}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {missionForm.mission_scope === "MULTI_GROUP" ? (
              <div className={classes.formField}>
                <div className={classes.metaLabel}>Select groups</div>
                <div className={classes.checkboxList}>
                  {groups.map(group => (
                    <FormControlLabel
                      key={`group-checkbox-${group.id}`}
                      control={
                        <Checkbox
                          checked={missionForm.group_ids.includes(group.id)}
                          onChange={this.toggleMissionGroup(group.id)}
                          color="primary"
                        />
                      }
                      label={group.name}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            <TextField
              className={classes.formField}
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={missionForm.description}
              onChange={this.handleMissionChange("description")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              label="Start date"
              type="datetime-local"
              value={missionForm.start_date}
              onChange={this.handleMissionChange("start_date")}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.formField}
              fullWidth
              label="End date"
              type="datetime-local"
              value={missionForm.end_date}
              onChange={this.handleMissionChange("end_date")}
              InputLabelProps={{ shrink: true }}
            />
            <div className={classes.helperText}>
              Use self scope for personal challenges, or attach one or more
              groups for shared missions.
            </div>
            <div className={classes.modalActions}>
              <Button color="transparent" onClick={this.closeMissionModal}>
                Cancel
              </Button>
              <Button
                color="info"
                onClick={this.submitMission}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingMission
                    ? "Update Mission"
                    : "Create Mission"}
              </Button>
            </div>
          </Box>
        </Modal>

        <Modal
          className={classes.modal}
          open={goalModalOpen}
          onClose={this.closeGoalModal}
        >
          <Box className={classes.modalBox}>
            <h3 className={classes.modalTitle}>
              {editingGoal ? "Edit Goal" : "Create Goal for Mission"}
            </h3>
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Mission"
              value={goalForm.mission}
              onChange={this.handleGoalChange("mission")}
              SelectProps={this.getSelectProps()}
            >
              {missions.map(mission => (
                <MenuItem key={`goal-mission-${mission.id}`} value={mission.id}>
                  {mission.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className={classes.formField}
              fullWidth
              label="Goal name"
              value={goalForm.name}
              onChange={this.handleGoalChange("name")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              select
              label="Goal type"
              value={goalForm.goal_type}
              onChange={this.handleGoalChange("goal_type")}
              SelectProps={this.getSelectProps()}
            >
              <MenuItem value="DISTANCE">Distance</MenuItem>
              <MenuItem value="TIME">Time</MenuItem>
            </TextField>
            <TextField
              className={classes.formField}
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={goalForm.description}
              onChange={this.handleGoalChange("description")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              label="Target metric"
              value={goalForm.target_metric}
              onChange={this.handleGoalChange("target_metric")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              type="number"
              label="Target value"
              value={goalForm.target_value}
              onChange={this.handleGoalChange("target_value")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              type="date"
              label="Target date"
              value={goalForm.target_date}
              onChange={this.handleGoalChange("target_date")}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={goalForm.is_shared}
                  onChange={this.handleGoalChange("is_shared")}
                  color="primary"
                />
              }
              label="Shared goal"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={goalForm.is_public}
                  onChange={this.handleGoalChange("is_public")}
                  color="primary"
                />
              }
              label="Public goal"
            />
            <div className={classes.modalActions}>
              <Button color="transparent" onClick={this.closeGoalModal}>
                Cancel
              </Button>
              <Button color="info" onClick={this.submitGoal} disabled={saving}>
                {saving
                  ? "Saving..."
                  : editingGoal
                    ? "Update Goal"
                    : "Create Goal"}
              </Button>
            </div>
          </Box>
        </Modal>
      </GridContainer>
    );
  }
}

Missions.propTypes = {
  axios: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired
};

export default withStyles(styles)(Missions);
